import { Button } from "@/components/ui/button";
import { Phone, Menu, X, Award, Shield, Star } from "lucide-react";
import { useState, useEffect } from "react";
import Logo from "./Logo";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const navItems = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Gallery", href: "#gallery" },
    { name: "Estimate", href: "#estimate" },
    { name: "Contact", href: "#contact" },
  ];

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsMenuOpen(false);
    
    // Add delay for mobile touch events and menu closing
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest' 
        });
      }
    }, 150);
  };

  return (
    <>
      {/* Trust Bar */}
      <div className="bg-gradient-gold text-white py-2 text-center text-sm font-medium">
        <div className="container mx-auto px-4 flex items-center justify-center gap-6">
          <div className="flex items-center gap-1">
            <Shield className="w-4 h-4" />
            <span>Licensed & Insured</span>
          </div>
          <div className="flex items-center gap-1">
            <Award className="w-4 h-4" />
            <span>15+ Years Experience</span>
          </div>
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 fill-current" />
            <span>4.9/5 Rating</span>
          </div>
        </div>
      </div>

      <header className={`fixed top-8 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-background/98 backdrop-blur-md border-b border-border shadow-medium' 
          : 'bg-background/95 backdrop-blur-sm shadow-soft'
      }`}>
        <div className="container mx-auto px-4">
          <div className={`flex items-center justify-between transition-all duration-300 ${
            isScrolled ? 'h-14' : 'h-16'
          }`}>
            {/* Logo */}
            <div className="flex items-center">
              <Logo />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={(e) => handleNavClick(item.href, e)}
                  className="text-foreground hover:text-primary transition-colors duration-200 font-medium relative group"
                >
                  {item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-accent transition-all duration-300 group-hover:w-full"></span>
                </button>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center space-x-6">
              <a 
                href="tel:+12049149814" 
                className="flex items-center space-x-2 text-foreground hover:text-gold-primary transition-colors group"
              >
                <div className="p-2 rounded-full bg-green-light group-hover:bg-gold-light transition-colors">
                  <Phone className="w-4 h-4 text-green-accent group-hover:text-gold-primary" />
                </div>
                <div className="text-left">
                  <div className="text-xs text-muted-foreground">Call Now</div>
                  <div className="font-semibold">(204) 914-9814</div>
                </div>
              </a>
              <Button variant="gold" size="lg" className="shadow-medium">
                Get 30% OFF Quote
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-foreground hover:text-primary transition-colors rounded-lg hover:bg-muted"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
        </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="lg:hidden py-6 border-t border-border animate-slide-up bg-background/98">
              <div className="flex flex-col space-y-6">
                {navItems.map((item) => (
                  <button
                    key={item.name}
                    onClick={(e) => handleNavClick(item.href, e)}
                    className="text-left text-foreground hover:text-primary transition-colors duration-200 font-medium py-2 text-lg"
                  >
                    {item.name}
                  </button>
                ))}
                <div className="pt-6 border-t border-border space-y-4">
                  <a 
                    href="tel:+12049149814" 
                    className="flex items-center space-x-3 text-foreground hover:text-primary transition-colors py-3 px-4 rounded-lg hover:bg-muted"
                  >
                    <div className="p-2 rounded-full bg-green-light">
                      <Phone className="w-5 h-5 text-green-accent" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Call Now</div>
                      <div className="font-semibold text-lg">(204) 914-9814</div>
                    </div>
                  </a>
                  <Button variant="gold" size="lg" className="w-full shadow-medium">
                    Get 30% OFF Quote
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;